/**
 * Vendor portal API client — thin wrapper around axios that hits
 * `/api/vendor/*` and `/api/admin/vendors/*` routes. Cookies are sent
 * automatically via `axios.defaults.withCredentials = true`.
 */
import axios, { API } from "@/lib/api";

// ============= Vendor-facing =============
export async function vendorSignup(payload) {
  const { data } = await axios.post(`${API}/vendor/auth/signup`, payload);
  return data;
}

export async function vendorLogin({ email, password }) {
  const { data } = await axios.post(`${API}/vendor/auth/login`, { email, password });
  return data;
}

export async function vendorGoogleLogin(idToken) {
  const { data } = await axios.post(`${API}/vendor/auth/google-login`, { id_token: idToken });
  return data;
}

export async function vendorGoogleSignup({ idToken, businessName, phone }) {
  const { data } = await axios.post(`${API}/vendor/auth/google-signup`, {
    id_token: idToken, business_name: businessName, phone,
  });
  return data;
}

export async function vendorLogout() {
  const { data } = await axios.post(`${API}/vendor/auth/logout`);
  return data;
}

export async function vendorMe() {
  const { data } = await axios.get(`${API}/vendor/me`);
  return data;
}

export async function vendorDashboard() {
  const { data } = await axios.get(`${API}/vendor/dashboard`);
  return data;
}

export async function vendorReferrals({ q, status, limit, skip } = {}) {
  const params = { q, status, limit, skip };
  const { data } = await axios.get(`${API}/vendor/referrals`, { params });
  return data;
}

export async function vendorCommissions({ status, limit, skip } = {}) {
  const params = { status, limit, skip };
  const { data } = await axios.get(`${API}/vendor/commissions`, { params });
  return data;
}

export async function vendorPayments({ limit, skip } = {}) {
  const params = { limit, skip };
  const { data } = await axios.get(`${API}/vendor/payments`, { params });
  return data;
}

// ============= Admin-facing =============
export async function adminListVendors({ status, limit, skip } = {}) {
  const params = { status, limit, skip };
  const { data } = await axios.get(`${API}/admin/vendors`, { params });
  return data;
}

export async function adminGetVendor(vendorId) {
  const { data } = await axios.get(`${API}/admin/vendors/${vendorId}`);
  return data;
}

export async function adminSetVendorStatus(vendorId, status) {
  const { data } = await axios.post(`${API}/admin/vendors/${vendorId}/status`, { status });
  return data;
}

export async function adminSetCommissionRate(vendorId, rate) {
  const { data } = await axios.post(
    `${API}/admin/vendors/${vendorId}/commission-rate`,
    { commission_rate: rate },
  );
  return data;
}

export async function adminListAllCommissions({ status, vendorId, limit, skip } = {}) {
  const params = { status, vendor_id: vendorId, limit, skip };
  const { data } = await axios.get(`${API}/admin/vendors/commissions/all`, { params });
  return data;
}

export async function adminMarkCommissionPaid(commissionId, reference) {
  const { data } = await axios.post(
    `${API}/admin/vendors/commissions/${commissionId}/mark-paid`,
    { reference: reference || null },
  );
  return data;
}

export async function adminMarkCommissionPending(commissionId) {
  const { data } = await axios.post(
    `${API}/admin/vendors/commissions/${commissionId}/mark-pending`,
  );
  return data;
}


// ============= Phase 2 — Analytics + Payouts =============
export async function vendorAnalyticsSummary({ rangeDays = 30 } = {}) {
  const { data } = await axios.get(`${API}/vendor/analytics/summary`, {
    params: { range_days: rangeDays },
  });
  return data;
}

export async function vendorPayouts({ limit = 50, skip = 0 } = {}) {
  const { data } = await axios.get(`${API}/vendor/payouts`, {
    params: { limit, skip },
  });
  return data;
}

// ============= Phase 2 — Admin: Payouts =============
export async function adminCreatePayout(vendorId, { reference, note } = {}) {
  const { data } = await axios.post(
    `${API}/admin/vendors/${vendorId}/payouts`,
    { reference: reference || null, note: note || null },
  );
  return data;
}

export async function adminListVendorPayouts(vendorId, { limit = 100, skip = 0 } = {}) {
  const { data } = await axios.get(
    `${API}/admin/vendors/${vendorId}/payouts`,
    { params: { limit, skip } },
  );
  return data;
}

// ============= Phase 2 — Admin: Audit Log =============
export async function adminAuditLog({ action, actorId, targetType, sinceHours, limit = 100, skip = 0 } = {}) {
  const params = { action, actor_id: actorId, target_type: targetType, since_hours: sinceHours, limit, skip };
  const { data } = await axios.get(`${API}/admin/audit-log`, { params });
  return data;
}

export async function adminAuditLogActions() {
  const { data } = await axios.get(`${API}/admin/audit-log/actions`);
  return data;
}

// ============= Phase 2 — Admin: Attribution Rate =============
export async function adminAttributionRate({ rangeDays = 30 } = {}) {
  const { data } = await axios.get(`${API}/admin/metrics/attribution-rate`, {
    params: { range_days: rangeDays },
  });
  return data;
}

// ============= iter 92 — Payout status + Vendor UPI =============
export async function adminUpdatePayoutStatus(vendorId, payoutId, { status, reference, note } = {}) {
  const { data } = await axios.post(
    `${API}/admin/vendors/${vendorId}/payouts/${payoutId}/status`,
    { status, reference: reference || null, note: note || null },
  );
  return data;
}

export async function adminSetVendorUpi(vendorId, upiId) {
  const { data } = await axios.put(
    `${API}/admin/vendors/${vendorId}/upi`,
    { upi_id: upiId || "" },
  );
  return data;
}

export async function vendorUpdateOwnUpi(upiId) {
  const { data } = await axios.put(
    `${API}/vendor/me/upi`,
    { upi_id: upiId || "" },
  );
  return data;
}

// ============= iter 93 — Vendor Profile (UPI + bank + GST) =============
export async function vendorGetProfile() {
  const { data } = await axios.get(`${API}/vendor/me/profile`);
  return data;
}

export async function vendorUpdateProfile(fields) {
  // fields: { upi_id?, bank_account_holder?, bank_account_number?, bank_ifsc?, gst_number? }
  // Omit any key you don't want to touch. Empty string clears a field.
  const { data } = await axios.put(`${API}/vendor/me/profile`, fields);
  return data;
}
